<script setup>
/**
 * BS Modal
 */
import { computed, onMounted, onUnmounted, ref, shallowRef, watchEffect } from 'vue'
import { storeToRefs } from 'pinia'
import { Modal } from 'bootstrap'
import { i18n } from '@/i18n'
import { useModalStack } from '@/stores/modalstack'
import { resetPageScrollLock } from '@/lib/pageScrollLock'

const emit = defineEmits(['hidden', 'shown'])
const modal = ref(null)
const props = defineProps({
  size: String,
  header: {
    type: Boolean,
    default: true,
  },
  classes: [Array, Object, String, null],
  modalClass: {
    type: String,
    default: '',
  },
  backdrop: {
    type: Boolean,
    default: undefined,
  },
  blocking: {
    type: Boolean,
    default: false,
  },
  close: {
    type: Boolean,
    default: true,
  },
  className: [String, null],
  useHistory: {
    type: Boolean,
    default: true,
  },
  /** Без fade: сразу убрать окно, не анимировать тяжёлое содержимое (многостраничный PDF). */
  instantDismiss: {
    type: Boolean,
    default: false,
  },
  closeAriaLabel: {
    type: String,
    default: '',
  },
})
let mbs = ref(null)
/** i18n.global — Mod также монтируется через createVNode (useConfirm) без app context. */
const closeLabel = computed(
  () => props.closeAriaLabel || i18n.global.t('common.close')
)

const modalStack = useModalStack()
const { stack } = storeToRefs(modalStack)

let pushed = false
let closingByPop = false
let ignorePop = false
let hiddenEmitted = false
let hideWatchdog = 0
let dismissing = false
/** Корень модалки: к onUnmounted template ref может быть уже снят. */
let modalEl = null
/** Идёт анимация Bootstrap: show.bs → shown.bs или hide.bs → hidden.bs. */
let transitioning = false

function onTransitionStart() {
  transitioning = true
}

function onTransitionEnd() {
  transitioning = false
}

/** Не даёт Bootstrap закрыть уже открытую модалку, когда blocking сменился на true. */
function preventBlockingDismiss(event) {
  if (!props.blocking) return
  if (event.type === 'keydown' && event.key === 'Escape') {
    event.preventDefault()
    event.stopImmediatePropagation()
  }
  if (event.type === 'click' && event.target === modal.value) {
    event.preventDefault()
    event.stopImmediatePropagation()
  }
}

const modalClassMerged = computed(() => {
  const parts = []
  if (props.modalClass) {
    parts.push(props.modalClass)
  }
  if (props.classes) {
    parts.push(props.classes)
  }
  return parts.filter(Boolean).join(' ')
})

function restoreBodyScroll() {
  if (stack.value.length === 0) {
    resetPageScrollLock()
  }
}

function onPopState() {
  if (ignorePop) {
    ignorePop = false
    return
  }

  if (stack.value.length > 0) {
    closingByPop = true
    modalStack.pop(null)
  }
}

function onShown() {
  stackDocviewerZIndex()
  emit('shown')
}

function isDocviewerOverParent(modalClass) {
  const c = String(modalClass || '')
  return (
    c.includes('docviewer-from-history') ||
    c.includes('docviewer-over-parent')
  )
}

function stackDocviewerZIndex() {
  if (!modal.value || !isDocviewerOverParent(props.modalClass)) return
  const modals = [...document.querySelectorAll('.modal.show')].filter(
    el => !el.classList.contains('modalstack-overlay')
  )
  const idx = modals.indexOf(modal.value)
  const layer = idx >= 0 ? idx : modals.length
  // Выше modalstack (1320), ниже confirm (1400)
  const z = 1350 + layer * 20
  modal.value.style.zIndex = String(z)
}

onMounted(() => {
  // Рендер слота мог упасть: Vue подменяет корень на comment, ref пустой.
  // new Modal(undefined) потом читает _config.backdrop и даёт второй тост.
  if (!modal.value) return
  mbs.value = new Modal(modal.value, {
    backdrop: props.backdrop ?? true,
    keyboard: true,
  })
  // При blocking не даём закрыть уже открытую модалку по Esc или клику снаружи.
  modal.value.addEventListener('keydown', preventBlockingDismiss, true)
  modal.value.addEventListener('click', preventBlockingDismiss, true)
  modal.value.addEventListener('hidden.bs.modal', onHidden)
  modal.value.addEventListener('hide.bs.modal', onHide)
  modal.value.addEventListener('shown.bs.modal', onShown)
  modalEl = modal.value
  for (const name of ['show.bs.modal', 'hide.bs.modal']) {
    modalEl.addEventListener(name, onTransitionStart)
  }
  for (const name of ['shown.bs.modal', 'hidden.bs.modal']) {
    modalEl.addEventListener(name, onTransitionEnd)
  }
  mbs.value.show()

  if (props.useHistory) {
    try {
      history.pushState({ modal: true }, '')
      pushed = true
    } catch (e) {
      console.error('Failed to push state', e)
      pushed = false
    }
  }

  if (props.useHistory) {
    window.addEventListener('popstate', onPopState)
  }
})

/**
 * Уничтожить Bootstrap Modal; посреди анимации — дождаться её конца.
 * Bootstrap 5.3 не отменяет анимацию в dispose: отложенный колбэк потом читает
 * обнулённый _config («reading 'focus'») — при быстром уходе со страницы
 * (двойной «назад» во время открытия просмотрщика).
 *
 * @param {Modal} instance экземпляр Bootstrap Modal.
 * @param {HTMLElement|null} el корень модалки.
 */
function disposeAfterTransition(instance, el) {
  if (!transitioning || !el) {
    instance.dispose()
    return
  }
  let done = false
  let timer = 0
  const finish = () => {
    if (done) {
      return
    }
    done = true
    clearTimeout(timer)
    el.removeEventListener('shown.bs.modal', finish)
    el.removeEventListener('hidden.bs.modal', finish)
    instance.dispose()
    // Vue элемент уже снял, но Bootstrap в _showElement возвращает модалку в body.
    el.remove()
  }
  el.addEventListener('shown.bs.modal', finish)
  el.addEventListener('hidden.bs.modal', finish)
  // Запас, если shown/hidden не придёт (hide отменён): дольше fade модалки и подложки.
  timer = window.setTimeout(finish, 1000)
}

onUnmounted(() => {
  if (mbs.value) {
    teardownBootstrapOverlay(mbs.value)
    disposeAfterTransition(mbs.value, modalEl)
    mbs.value = undefined
  }

  if (modal.value) {
    modal.value.removeEventListener('keydown', preventBlockingDismiss, true)
    modal.value.removeEventListener('click', preventBlockingDismiss, true)
    modal.value.removeEventListener('hidden.bs.modal', onHidden)
    modal.value.removeEventListener('hide.bs.modal', onHide)
    modal.value.removeEventListener('shown.bs.modal', onShown)
  }
  if (modalEl) {
    for (const name of ['show.bs.modal', 'hide.bs.modal']) {
      modalEl.removeEventListener(name, onTransitionStart)
    }
    for (const name of ['shown.bs.modal', 'hidden.bs.modal']) {
      modalEl.removeEventListener(name, onTransitionEnd)
    }
  }

  restoreBodyScroll()
  scrubOrphanModalDom()
  clearHideWatchdog()
  if (props.useHistory) {
    window.removeEventListener('popstate', onPopState)
  }
})

defineExpose({ hide: requestHide })

function clearHideWatchdog() {
  if (!hideWatchdog) {
    return
  }
  clearTimeout(hideWatchdog)
  hideWatchdog = 0
}

/**
 * Bootstrap иногда оставляет _isTransitioning и не шлёт hidden.bs.modal
 * (крестик после этого уже не вызывает hide). Дожимаем закрытие сами.
 */
function forceFinishHide() {
  const el = modal.value
  const instance = mbs.value
  if (!el || hiddenEmitted) {
    return
  }
  el.classList.remove('show')
  el.style.display = 'none'
  el.setAttribute('aria-hidden', 'true')
  if (instance) {
    instance._isShown = false
    instance._isTransitioning = false
    transitioning = false
    teardownBootstrapOverlay(instance)
  }
  document.querySelectorAll('.modal-backdrop').forEach(node => node.remove())
  scrubOrphanModalDom()
  onHidden()
}

function armHideWatchdog() {
  clearHideWatchdog()
  const el = modal.value
  if (!el) {
    return
  }
  hideWatchdog = window.setTimeout(() => {
    hideWatchdog = 0
    if (!el.isConnected || hiddenEmitted) {
      return
    }
    if (el.classList.contains('show') || el.style.display === 'block') {
      forceFinishHide()
    }
  }, 500)
}

/**
 * Видима ли модалка: `.show` + не `display:none` (после dismissNow класс
 * могли оставить вложенные/сиротские узлы — по ним нельзя решать cleanup).
 *
 * @param {Element} el корень `.modal`.
 * @returns {boolean}
 */
function isModalVisiblyOpen(el) {
  if (!(el instanceof HTMLElement) || !el.classList.contains('show')) {
    return false
  }
  if (el.style.display === 'none') {
    return false
  }
  return getComputedStyle(el).display !== 'none'
}

/**
 * Снять сиротские backdrop/modal-open, если нет видимых модалок.
 * Иначе после просмотрщика/пароля клики по меню и Finder «мёртвые».
 */
function scrubOrphanModalDom() {
  const open = [...document.querySelectorAll('.modal.show')].some(isModalVisiblyOpen)
  if (open) {
    return
  }
  document.querySelectorAll('.modal-backdrop').forEach(node => node.remove())
  document.body.classList.remove('modal-open')
  document.documentElement.classList.remove('modal-open')
  document.body.style.removeProperty('overflow')
  document.body.style.removeProperty('padding-right')
  // Bootstrap ScrollBarHelper мог оставить padding; position/top — от pageScrollLock.
  if (!document.querySelector('.modalstack-overlay.modal.show')) {
    document.body.style.removeProperty('position')
    document.body.style.removeProperty('top')
    document.body.style.removeProperty('width')
    document.body.style.removeProperty('left')
    document.body.style.removeProperty('right')
  }
}

/**
 * Снять FocusTrap и backdrop Bootstrap без hide()-анимации.
 * dismissNow раньше не вызывал hide() — trap оставался и блокировал клики по Досье.
 *
 * @param {object|undefined|null} instance экземпляр Bootstrap Modal.
 */
function teardownBootstrapOverlay(instance) {
  if (!instance) {
    return
  }
  try {
    instance._focustrap?.deactivate()
  } catch {
    /* уже снят */
  }
  try {
    instance._backdrop?.dispose()
  } catch {
    try {
      instance._backdrop?.hide(() => {})
    } catch {
      /* backdrop уже снят */
    }
  }
}

/**
 * Сразу убирает окно и подложку, без fade по всему дереву страниц.
 * Разбор текстовых слоёв — уже после отрисовки закрытого экрана.
 */
function dismissNow() {
  if (hiddenEmitted || dismissing) {
    return
  }
  dismissing = true
  const el = modal.value
  if (!el) {
    onHidden()
    return
  }
  el.classList.remove('fade', 'show')
  el.style.display = 'none'
  el.setAttribute('aria-hidden', 'true')
  const instance = mbs.value
  if (instance) {
    instance._isShown = false
    instance._isTransitioning = false
    transitioning = false
    teardownBootstrapOverlay(instance)
  }
  // Все подложки: вложенный пароль мог оставить лишние.
  document.querySelectorAll('.modal-backdrop').forEach(node => node.remove())
  scrubOrphanModalDom()
  requestAnimationFrame(() => {
    setTimeout(() => onHidden(), 0)
  })
}

/** Просмотрщик закрываем без fade: снятие PDF не должно держать окно. */
function isDocViewer(el) {
  return Boolean(el?.querySelector?.('.docview-ui'))
}

/** Закрыть модалку, даже если прошлый hide() застрял в переходе Bootstrap. */
function requestHide() {
  const el = modal.value
  if (props.instantDismiss || isDocViewer(el)) {
    dismissNow()
    return
  }
  const instance = mbs.value
  if (!instance || !el) {
    onHidden()
    return
  }
  if (instance._isTransitioning) {
    instance._isTransitioning = false
  }
  if (!instance._isShown) {
    instance._isShown = true
  }
  instance.hide()
}

function onHide(event) {
  if (props.instantDismiss || isDocViewer(modal.value)) {
    event?.preventDefault?.()
    dismissNow()
    return
  }
  const active = document.activeElement
  if (active && active !== document.body && typeof active.blur === 'function') {
    active.blur()
  }
  armHideWatchdog()
}

function onHidden() {
  if (hiddenEmitted) {
    return
  }
  hiddenEmitted = true
  clearHideWatchdog()
  scrubOrphanModalDom()
  emit('hidden')
  restoreBodyScroll()
  // После emit родитель может снять вложенные модалки (пароль) — дочищаем на следующем кадре.
  requestAnimationFrame(() => {
    scrubOrphanModalDom()
  })

  if (!props.useHistory || !pushed) return

  if (closingByPop) {
    closingByPop = false
    pushed = false
    return
  }
  try {
    ignorePop = true
    history.back()
  } catch (e) {
    console.error('Failed to push state', e)
  } finally {
    pushed = false
  }
}

const clDialog = shallowRef({})
watchEffect(() => {
  clDialog.value = { ...clDialog.value, [props.className]: props.className }
  if (props.size == 'sm') {
    clDialog.value = { ...clDialog.value, 'modal-sm': true }
  }
  if (props.size == 'lg') {
    clDialog.value = { ...clDialog.value, 'modal-lg': true }
  }
  if (props.size == 'xl') {
    clDialog.value = { ...clDialog.value, 'modal-xl': true }
  }
  if (props.size == 'fit-content') {
    clDialog.value = { ...clDialog.value, 'fit-content': true }
  }
  if (props.size == 'full') {
    clDialog.value = { ...clDialog.value, 'modal-fullscreen': true }
  }
})
</script>
<template>
  <Teleport to="body">
    <div
      class="modal fade"
      :class="modalClassMerged"
      tabindex="-1"
      ref="modal"
    >
      <div
        class="modal-dialog"
        :class="[clDialog, { 'modal-dialog-centered': size !== 'full' }]"
      >
        <div class="modal-content mod-content-root">
          <div
            class="modal-header"
            v-if="header"
          >
            <slot name="header" />
            <button
              type="button"
              class="btn-close"
              :aria-label="closeLabel"
              @click="requestHide"
              v-if="close"
            />
          </div>
          <div class="modal-body">
            <slot
              name="default"
              :mbs="mbs"
            />
          </div>
          <div
            class="modal-body"
            v-if="$slots.secondbody"
          >
            <slot name="secondbody" />
          </div>
          <div
            class="modal-footer"
            v-if="$slots.footer"
          >
            <slot name="footer" />
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style>
.mod-content-root {
  position: relative;
  overflow: hidden;
}
</style>
